import { EMPTY, type Observable, switchMap } from 'rxjs'
import { action, observable, runInAction } from 'mobx'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type OpportunityStore } from './OpportunityStore'
import type { NavigateFn } from '@/RootStore'
import { type JobEvent } from '@/features/jobs/JobEvent'
import { type Member } from '@/features/iam/members/Member'
import type {
  FetchBookedConsultationsFn,
  FetchProOpenJobCountsFn,
  PickupConsultationJobFn,
} from '@/features/jobs'
import type {
  FetchUserDataFn,
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
} from '@/features/iam'
import { type ConsultationModalViewModel } from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'
import { type WorkspacePostingOpportunityStore } from '@/features/opportunities/workspace-postings-opportunities/WorkspacePostingOpportunityStore'
import { TabController } from '@/infra/TabController'
import { OpportunitiesViewModel } from './OpportunitiesViewModel'
import { WorkspacePostingOpportunitiesViewModel } from '@/features/opportunities/workspace-postings-opportunities/WorkspacePostingOpportunitiesViewModel'
import { task } from 'mobx-task'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import type { Id } from '@/infra/id'

/**
 * View model for the opportunities page and its tabs.
 */
export class OpportunitiesRootViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The opportunity the route asks to view.
   */
  @observable private accessor _requestedOpportunityId: Id | null = null

  /**
   * Whether the user may view job opportunities, as of the last load.
   */
  @observable private accessor _canViewJobOpportunities = false

  /**
   * TabController for the Opportunities tabs.
   */
  readonly opportunitiesTabController = new TabController<OpportunitiesTab>(
    () => [
      {
        tab: 'Jobs',
        title: 'Available jobs',
        hasError: () => false,
      },
      {
        tab: 'Workspaces',
        title: 'Outsourcing posts',
        hasError: () => false,
      },
    ],
  )

  /**
   * The Jobs tab.
   */
  readonly opportunitiesVm

  /**
   * The Workspaces tab.
   */
  readonly workspaceOpportunitiesVm

  /**
   * Whether the user may view both tabs.
   */
  @observable accessor showTabs = false

  /**
   * Desktop notifications for opportunities that become available.
   */
  readonly systemNotifications$

  constructor(
    private readonly _opportunityStore: OpportunityStore,
    private readonly _fetchBookedConsultations: FetchBookedConsultationsFn,
    private readonly _pickupConsultationJob: PickupConsultationJobFn,
    private readonly _fetchProOpenJobCounts: FetchProOpenJobCountsFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
    private readonly _fetchUserData: FetchUserDataFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _navigate: NavigateFn,
    private readonly _jobEvents$: Observable<JobEvent>,
    private readonly _currentMember$: Observable<Member>,
    private readonly _consultationModalVm: ConsultationModalViewModel,
    private readonly _workspacePostingOpportunityStore: WorkspacePostingOpportunityStore,
  ) {
    this.opportunitiesVm = new OpportunitiesViewModel(
      this._opportunityStore,
      this._fetchBookedConsultations,
      this._pickupConsultationJob,
      this._fetchProOpenJobCounts,
      this._getCurrentUserWorkspaceMemberships,
      this._listWorkspaceMemberships,
      this._getTeamMembersForWorkspaceMember,
      this._fetchUserData,
      this._getUserData,
      this._getCurrentMember,
      this._newAlert,
      this._createAlertRetryWrapper,
      this._createAlertErrorHandler,
      this._navigate,
      this._jobEvents$,
      this._currentMember$,
      this._consultationModalVm,
    )

    this.workspaceOpportunitiesVm = new WorkspacePostingOpportunitiesViewModel(
      this._workspacePostingOpportunityStore,
      this._createAlertRetryWrapper,
      this._createAlertErrorHandler,
      this._getCurrentUserWorkspaceMemberships,
    )

    this.systemNotifications$ = this.opportunitiesVm.systemNotifications$

    this._lifecycle.manage(
      toStream(
        () =>
          this.fetch.resolved && this._canViewJobOpportunities
            ? this._requestedOpportunityId
            : null,
        true,
      ).pipe(
        switchMap((opportunityId) =>
          opportunityId
            ? withAbortSignal((signal) =>
                this.opportunitiesVm.viewOpportunity(opportunityId, signal),
              )
            : EMPTY,
        ),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Checks which opportunities the user may view and picks the tab.
   */
  fetch = task(async (signal: AbortSignal) => {
    const [canViewOps, canViewWorkspaceOps] = await Promise.all([
      this._opportunityStore.probeCanViewOpportunities(),
      this._workspacePostingOpportunityStore.canViewWorkspaceOpportunities(),
    ])
    signal.throwIfAborted()

    runInAction(() => {
      if (canViewOps) {
        this.opportunitiesTabController.change('Jobs')
      } else if (canViewWorkspaceOps) {
        this.opportunitiesTabController.change('Workspaces')
      }
      this.showTabs = canViewOps && canViewWorkspaceOps
      this._canViewJobOpportunities = canViewOps
    })
  })

  /**
   * Views the opportunity once the view has loaded, or none.
   */
  @action.bound
  requestOpportunity(opportunityId: Id | null) {
    this._requestedOpportunityId = opportunityId
  }
}

/**
 * Opportunities section tab.
 */
export type OpportunitiesTab = 'Jobs' | 'Workspaces'
